import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useUserPartnerInformation } from "../../../hooks/useUserPartnerInformation";
import { auth } from "../../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { ROUTES } from "../../../routes/Routes";
import { importProductBase } from "../../../services/product/product.api";

const REQUIRED_FIELDS = [
    { key: "sku", label: "SKU (Required)", required: true },
    { key: "brand", label: "Brand", required: true },
    { key: "model", label: "Model", required: true },
    { key: "price", label: "Price", required: false },
    { key: "description", label: "Description", required: false },
    { key: "year", label: "Year", required: false },
    { key: "edition", label: "Edition", required: false },
];

const ProductImport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const { userPartnerInformation } = useUserPartnerInformation(user?.uid);

    // Steps: upload, mapping, preview, uploading, result
    const [step, setStep] = useState("upload");
    const [fileData, setFileData] = useState<any[]>([]);
    const [fileHeaders, setFileHeaders] = useState<string[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [uploadStats, setUploadStats] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();

        if (file.name.endsWith(".csv")) {
            reader.onload = (event) => {
                const text = event.target?.result as string;
                Papa.parse(text, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results: any) => {
                        setFileHeaders(results.meta.fields || []);
                        setFileData(results.data);
                        setStep("mapping");
                    },
                });
            };
            reader.readAsText(file);
        } else {
            // Excel
            reader.onload = (event) => {
                const data = new Uint8Array(event.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);
                if (json.length > 0) {
                    setFileHeaders(Object.keys(json[0] as any));
                    setFileData(json);
                    setStep("mapping");
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const autoMap = () => {
        const newMapping: Record<string, string> = {};
        REQUIRED_FIELDS.forEach((field) => {
            const match = fileHeaders.find(
                (h) => h.toLowerCase() === field.key.toLowerCase() || h.toLowerCase().includes(field.key)
            );
            if (match) newMapping[field.key] = match;
        });
        setMapping(newMapping);
    };

    const handleMappingChange = (frydaField: string, fileColumn: string) => {
        setMapping((prev) => ({ ...prev, [frydaField]: fileColumn }));
    };

    const handleImport = async () => {
        setStep("uploading");
        setError(null);

        // Transform data
        const transformedData = fileData.map((row) => {
            const newRow: any = {};
            Object.entries(mapping).forEach(([frydaKey, fileCol]) => {
                if (fileCol) {
                    newRow[frydaKey] = row[fileCol];
                }
            });
            return newRow;
        });

        // Validate basic requirement (SKU)
        const validRows = transformedData.filter(r => r.sku);
        if (validRows.length === 0) {
            setError("No valid products found. SKU is required.");
            setStep("mapping");
            return;
        }

        try {
            const partnerId = userPartnerInformation?.data?.partnerId;
            if (!partnerId) throw new Error("Partner ID not found");

            const response = await importProductBase(partnerId, validRows);

            // API returns { success: true, data: stats }
            // Axios response structure depends on the API wrapper. 
            // Assuming response.data is the payload body.
            setUploadStats(response.data.data);
            setStep("result");
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.error?.message || err.message || "Import failed");
            setStep("result");
        }
    };

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <div className="mb-6 flex justify-between items-center">
                <h1 className="text-2xl font-bold">Import Product Base</h1>
                <button onClick={() => navigate(ROUTES.PRODUCTS)} className="text-gray-500 hover:text-black">
                    Cancel
                </button>
            </div>

            {step === "upload" && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:bg-gray-50 transition-colors">
                    <input
                        type="file"
                        accept=".csv, .xlsx, .xls"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                        <div className="text-gray-600 mb-2">Drag and drop your file here, or click to browse</div>
                        <div className="text-sm text-gray-400">Supports CSV and Excel</div>
                        <div className="mt-4 bg-black text-white px-4 py-2 rounded-md inline-block">
                            Select File
                        </div>
                    </label>
                </div>
            )}

            {step === "mapping" && (
                <div>
                    <div className="flex justify-between mb-4">
                        <h2 className="text-lg font-semibold">Map Columns</h2>
                        <button onClick={autoMap} className="text-blue-600 underline">Auto-Detect</button>
                    </div>
                    <div className="grid gap-4 mb-8">
                        {REQUIRED_FIELDS.map((field) => (
                            <div key={field.key} className="flex iems-center justify-between border-b pb-2">
                                <div>
                                    <div className="font-medium">{field.label}</div>
                                    <div className="text-xs text-gray-500">{field.key}</div>
                                </div>
                                <select
                                    className="border rounded p-2 w-1/2"
                                    value={mapping[field.key] || ""}
                                    onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                >
                                    <option value="">-- Select Column --</option>
                                    {fileHeaders.map((h) => (
                                        <option key={h} value={h}>{h}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={handleImport}
                        className="w-full bg-black text-white py-3 rounded-md font-bold"
                    >
                        Start Import
                    </button>
                </div>
            )}

            {step === "uploading" && (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
                    <p>Processing import...</p>
                    <p className="text-sm text-gray-500">This may take a moment for large files.</p>
                </div>
            )}

            {step === "result" && (
                <div className="text-center">
                    {error ? (
                        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">
                            Error: {error}
                        </div>
                    ) : (
                        <div className="bg-green-50 text-green-700 p-6 rounded-md mb-6">
                            <h3 className="text-xl font-bold mb-2">Import Complete!</h3>
                            <p>Successfully imported: {uploadStats?.imported || 0}</p>
                            <p>Failed rows: {uploadStats?.failed || 0}</p>
                            {uploadStats?.errors?.length > 0 && (
                                <div className="mt-4 text-left max-h-40 overflow-auto bg-white p-2 border">
                                    <p className="font-bold text-xs text-gray-500">Errors:</p>
                                    {uploadStats.errors.map((e: string, i: number) => (
                                        <div key={i} className="text-xs text-red-500">{e}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <button
                        onClick={() => navigate(ROUTES.PRODUCTS)}
                        className="bg-black text-white px-6 py-3 rounded-md"
                    >
                        Back to Products
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProductImport;
